import {Component, OnInit, Input, Output, EventEmitter, AfterViewInit, AfterViewChecked} from '@angular/core';
import {MessageService, MenuItem} from 'primeng/api';
import {XpressPaymentTransactionService} from 'app/services';
import * as moment from 'moment';
import {Utils} from 'app/utils';
import {CommonModule} from '@angular/common';
import {TabMenuModule} from 'primeng/tabmenu';
import {ScrollPanelModule} from 'primeng/scrollpanel';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {TableModule} from 'primeng/table';
import {ToastModule} from 'primeng/toast';
import {SidebarModule} from 'primeng/sidebar';
import {XpassOrdersTableRow} from '../xpass/interfaces/xpass-orders-table-row.interface';
import {DriverDetail} from './models/driver-detail.model';
import {
	PaymentDetail,
	PaymentMethod,
	TransactionStatus,
} from './models/payment-detail.model';
import {
	TripDetail,
	TripType,
	TripDirection,
	ApiTripType,
} from './models/trip-detail.model';
import {BookingScheduleDetail} from './models/booking-schedule-detail.model';
import {CustomerDetail} from './models/customer-detail.model';
import {TimelineModule} from 'primeng/timeline';
import {
	OrderTimelineInfo,
	TimelineEvent,
} from './models/order-timeline.model';
import {maskCustomerPhone} from './utils/phone-utils';
import {XpassAdminService} from './xpass-detail.service';
import {DialogModule} from 'primeng/dialog';
import {TransactionTypeEnum} from 'app/models/transaction/transaction-type.enum';
import {GetTransferOrderDetailsRequest} from './models/get-transfer-order-details-request.model';
import {TransferOrderDetailsDto} from './models/get-transfer-order-details-response.model';
import {ButtonDirective} from 'primeng/button';
import {OrderService} from 'app/ops-tower/xpass/services/order.service';
import {PaginatorModule} from 'primeng/paginator';
import {FormsModule} from '@angular/forms';
import {RegenerateQrCodeRequest} from './models/regenerate-transfer-order-request';
import {ONLY_DATE_FORMAT, ONLY_TIME_FORMAT} from 'app/ops-tower/xpass/constants/xpass-variables.constant';

enum MenuTab {
	TNXNDetails = 1,
	Customer = 2,
	Driver = 3,
	Payment = 4,
	Timeline = 5,
}

@Component({
	selector: 'xpass-booking-detail',
	standalone: true,
	imports: [
		CommonModule,
		TabMenuModule,
		ScrollPanelModule,
		ProgressSpinnerModule,
		TableModule,
		ToastModule,
		SidebarModule,
		TimelineModule,
		DialogModule,
		ButtonDirective,
		PaginatorModule,
		FormsModule,
	],
	templateUrl: './xpass-booking-detail.html',
	styleUrls: ['./xpass-booking-detail.scss'],
	providers: [MessageService],
})
export class XpassBookingDetailComponent implements OnInit, AfterViewInit, AfterViewChecked {
	@Input() transaction?: XpassOrdersTableRow;
	@Input() showXpassBookingDetailSideBar = false;
	@Output() showXpassBookingDetailSideBarChange = new EventEmitter<boolean>();

	public loading: boolean = false;
	public regeneratingQrCode: boolean = false;
	public menuTab = MenuTab;
	public activeMenuTab: MenuTab = MenuTab.TNXNDetails;
	public menuItems: MenuItem[] = [];
	public showCancelTripDialog: boolean = false;
	public copiedIPassIndex: number | null = null;
	private copiedIPassTimeout?: ReturnType<typeof setTimeout>;

	showAddNotes: boolean = false;
	showReassignDriver: boolean = false;
	transferNote: string = '';
	noteHeader: string = '';

	// API Response data
	public transferOrderDetails?: TransferOrderDetailsDto;
	// Legacy properties for existing template compatibility
	driverDetail!: DriverDetail;
	tripDetail!: TripDetail;
	bookingScheduleDetail!: BookingScheduleDetail;
	customerDetail!: CustomerDetail;
	paymentDetail!: PaymentDetail;
	orderTimelineInfo!: OrderTimelineInfo;
	events: TimelineEvent[] = [];
	eventsTimeline: TimelineEvent[] = [];
	mapZoom = 11;
	routePath: google.maps.PolylineOptions = {
		strokeColor: '#FF0000', // Red line
		strokeOpacity: 1.0,
		strokeWeight: 3
	};
	mapOptions: google.maps.MapOptions = {
		mapTypeControl: false
	};
	//Google maps autocomplete
	public map!: google.maps.Map;
	public geocoder!: google.maps.Geocoder;
	public marker!: google.maps.Marker;
	private geocodeTimer: number = 0;
	private mapInitialized = false;

	constructor(
		private messageService: MessageService,
		private xpressPaymentTransactionService: XpressPaymentTransactionService,
		private xpassAdminService: XpassAdminService,
		private xpassService: OrderService
	) {
	}

	ngOnInit(): void {
		this.initializeMenuItems();
		this.loadTransactionDetails();
	}

	ngAfterViewInit(): void {
	}

	ngAfterViewChecked(): void {
		if (this.activeMenuTab === this.menuTab.Timeline && !this.mapInitialized) {
			const mapElement = document.getElementById('map');
			if (mapElement) {
				this.initMap(mapElement);
				this.mapInitialized = true;
			}
		} else if (
			this.activeMenuTab === this.menuTab.Payment ||
			this.activeMenuTab === this.menuTab.Driver ||
			this.activeMenuTab === this.menuTab.Customer ||
			this.activeMenuTab === this.menuTab.TNXNDetails
		) {
			this.mapInitialized = false;
		}
	}

	private initMap(mapElement: HTMLElement): void {
		const mapOptions: google.maps.MapOptions = {
			center: {lat: 14.5995, lng: 120.9842},
			zoom: 12,
		};

		this.map = new google.maps.Map(mapElement, mapOptions);
		this.geocoder = new google.maps.Geocoder();

		const bounds = new google.maps.LatLngBounds();

		const pickupAddress = this.transaction?.PickupLocation;
		const dropOffAddress = this.transaction?.DropOffLocation;
		this.geocoder.geocode({address: pickupAddress}, (pickupResults, pickupStatus) => {
			if (pickupStatus === 'OK' && pickupResults !== null && pickupResults[0]) {
				const pickupLatLng = pickupResults[0].geometry.location;
				new google.maps.Marker({
					position: pickupLatLng,
					map: this.map,
					icon: {
						url: 'XpressApp/assets/images/live-map-icons/pickup.svg',
						scaledSize: new google.maps.Size(30, 30),
						anchor: new google.maps.Point(15, 15),
					},
					title: 'Pick-up Location',
				});
				bounds.extend(pickupLatLng);
				this.geocoder.geocode({address: dropOffAddress}, (dropResults, dropStatus) => {
					if (dropStatus === 'OK' && dropResults !== null && dropResults[0]) {
						const dropOffLatLng = dropResults[0].geometry.location;

						new google.maps.Marker({
							position: dropOffLatLng,
							map: this.map,
							icon: {
								url: 'XpressApp/assets/images/live-map-icons/destination.svg',
								scaledSize: new google.maps.Size(30, 30),
								anchor: new google.maps.Point(15, 15),
							},
							title: 'Drop-off Location',
						});

						bounds.extend(dropOffLatLng);
						this.map?.fitBounds(bounds);
						const routeLine = new google.maps.Polyline({
							path: [pickupLatLng, dropOffLatLng],
							geodesic: true,
							strokeColor: '#2196F3',
							strokeOpacity: 1.0,
							strokeWeight: 4,
						});
						routeLine.setMap(this.map);
					} else {
						console.error(dropStatus);
					}
				});
			} else {
				console.error(pickupStatus);
			}
		});
	}

	private loadTransactionDetails(): void {
		if (!this.transaction?.TransferOrderId) {
			this.messageService.add({
				severity: 'error',
				summary: 'Error',
				detail: 'Transfer Order ID is required',
			});
			return;
		}

		this.loading = true;
		this.clearCopiedIPassState();

		const request: GetTransferOrderDetailsRequest = {
			TransferOrderId: this.transaction.TransferOrderId,
			TimeZoneOffset: Utils.getTimezoneOffset(),
		};

		this.xpassAdminService
			.getTransferOrderDetails(request, this.messageService)
			.then((response) => {
				if (response.Success && response.TransferOrderDetails) {
					this.transferOrderDetails = response.TransferOrderDetails;

					// Update legacy properties for template compatibility
					this.updateLegacyProperties();
				} else {
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail:
							response.ErrorMessage || 'Failed to load transfer order details',
					});
				}
				this.loading = false;
			})
			.catch((error: any) => {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Failed to load transfer order details',
				});
				this.loading = false;
			});
	}

	private updateLegacyProperties(): void {
		if (!this.transferOrderDetails) {
			return;
		}

		// Update driver details
		if (this.transferOrderDetails.Driver) {
			this.driverDetail = {
				name: this.transferOrderDetails.Driver.Name,
				sex: this.transferOrderDetails.Driver.Sex || 'Not specified',
				phone: this.transferOrderDetails.Driver.Phone || 'Not specified',
				email: this.transferOrderDetails.Driver.Email || 'Not specified',
				dateOfBirth: this.transferOrderDetails.Driver.DateOfBirth
					? new Date(
						this.transferOrderDetails.Driver.DateOfBirth
					).toLocaleDateString()
					: 'Not specified',
			};
		}

		// Update trip details
		if (this.transferOrderDetails.Details) {
			this.tripDetail = {
				tripType: this.getTripType(this.transferOrderDetails.Details.TripType),
				tripDirection: this.getTripDirection(
					this.transferOrderDetails.Details.TripType
				),
				guestCount: this.transferOrderDetails.Details.Guests || 0,
				tripId:
					this.transferOrderDetails.Details.TransferOrderId?.toString() || '',
				tripDescription: this.transferOrderDetails.Details.TripType || '',
				specialRequirements: '',
			};
		}

		// Update booking schedule
		if (this.transferOrderDetails.Details) {
			this.bookingScheduleDetail = {
				arrivalDate: this.transferOrderDetails.Details.ScheduledDate
					? moment(Utils.utcToLocalDate(this.transferOrderDetails.Details.ScheduledDate)).format(ONLY_DATE_FORMAT)
					: '',
				arrivalTime: this.transferOrderDetails.Details.ScheduledDate
					? moment(Utils.utcToLocalDate(this.transferOrderDetails.Details.ScheduledDate)).format(ONLY_TIME_FORMAT)
					: '',
				modifiedSchedule: this.transferOrderDetails.Details.IsModified
					? 'Modified'
					: 'Original',
			};
		}

		// Update customer details
		if (this.transferOrderDetails.Customer) {
			this.customerDetail = {
				name: this.transferOrderDetails.Customer.Name,
				signupDate: this.transferOrderDetails.Customer.SignUpDate
					? new Date(
						this.transferOrderDetails.Customer.SignUpDate
					).toLocaleDateString()
					: '',
				signupDaysAgo: this.transferOrderDetails.Customer.DaysSinceSignUp || 0,
				phone: this.transferOrderDetails.Customer.Phone || '',
				phoneVerified: this.transferOrderDetails.Customer.IsPhoneVerified,
				rating: this.transferOrderDetails.Customer.Rating || 0,
				account: this.transferOrderDetails.Customer.AccountType || '',
				application: this.transferOrderDetails.Customer.Application || '',
				avatarUrl:
					this.transferOrderDetails.Customer.ProfileImageUrl ||
					'/Content/xpress-app/images/avatarbooking.png',
			};
		}

		// Update payment details
		if (this.transferOrderDetails.Payment) {
			this.paymentDetail = {
				transactionDate: this.transferOrderDetails.Payment.TransactionDate
					? new Date(
						this.transferOrderDetails.Payment.TransactionDate
					).toLocaleDateString()
					: '',
				transactionTime:
					this.transferOrderDetails.Payment.TransactionTime || '',
				transactionType: TransactionTypeEnum.Preauth,
				paymentMethod: this.transferOrderDetails.Payment.PaymentMethod || 'None',
				partnerTransactionId:
					this.transferOrderDetails.Payment.PartnerTransactionId || '',
				transactionAmount:
					this.transferOrderDetails.Payment.TransactionAmount?.toString() || '',
				transactionStatus: TransactionStatus.Paid,
				totalPaid:
					this.transferOrderDetails.Payment.TotalPaid?.toString() || '',
				tricycleTransfer:
					this.transferOrderDetails.Payment.TricycleTransfer || '',
				terminalFee: this.transferOrderDetails.Payment.TerminalFee || '',
				boatTransfer: this.transferOrderDetails.Payment.BoatTransfer || '',
				etrikeTransfer: this.transferOrderDetails.Payment.EtrikeTransfer || '',
				environmentalFee:
					this.transferOrderDetails.Payment.EnvironmentalFee || '',
				promo: this.transferOrderDetails.Payment.Promo || '',
				amountPaid: this.transferOrderDetails.Payment.AmountPaid || '',
				convenienceFee: this.transferOrderDetails.Payment.ConvenienceFee
			};
		}

		// Update timeline
		if (this.transferOrderDetails.Timeline) {
			this.orderTimelineInfo = {
				priority: this.transferOrderDetails.Timeline.Priority || '',
				sessionTime: this.transferOrderDetails.Timeline.SessionTime || '',
				events:
					this.transferOrderDetails.Timeline.Events?.map((event) => ({
						detail: event.Description || event.EventType,
						dateTime: event.EventDateTime
							? new Date(event.EventDateTime).toLocaleString()
							: '',
						status: event.IsCompleted
							? 'Done'
							: event.IsCurrent
								? 'Current'
								: 'Pending',
					})) || [],
			};
			this.events = this.orderTimelineInfo.events;
		}
	}

	private initializeMenuItems(): void {
		this.menuItems = [
			{
				label: 'Details',
				command: () => (this.activeMenuTab = MenuTab.TNXNDetails),
			},
			{
				label: 'Customer',
				command: () => (this.activeMenuTab = MenuTab.Customer),
			},
			{label: 'Driver', command: () => (this.activeMenuTab = MenuTab.Driver)},
			{
				label: 'Payment',
				command: () => (this.activeMenuTab = MenuTab.Payment),
			},
			{
				label: 'Timeline',
				command: () => (this.activeMenuTab = MenuTab.Timeline),
			},
		];
	}

	public cancel(): void {
		this.showXpassBookingDetailSideBar = false;
		this.showXpassBookingDetailSideBarChange.emit(false);
	}

	private clearCopiedIPassState(): void {
		if (this.copiedIPassTimeout) {
			clearTimeout(this.copiedIPassTimeout);
			this.copiedIPassTimeout = undefined;
		}
		this.copiedIPassIndex = null;
	}

	public copyIPassNumber(iPassNumber: string, index: number): void {
		if (!iPassNumber) {
			return;
		}

		navigator.clipboard.writeText(iPassNumber).then(() => {
			this.clearCopiedIPassState();

			this.copiedIPassIndex = index;
			this.copiedIPassTimeout = setTimeout(() => {
				this.copiedIPassIndex = null;
				this.copiedIPassTimeout = undefined;
			}, 2000);
		}).catch(() => {
			this.messageService.add({
				severity: 'error',
				summary: 'Error',
				detail: 'Unable to copy IPass number.',
			});
		});
	}

	public isIPassCopied(index: number): boolean {
		return this.copiedIPassIndex === index;
	}

	public openCancelTripDialog(): void {
		this.showCancelTripDialog = true;
	}

	public closeCancelTripDialog(): void {
		this.showCancelTripDialog = false;
	}

	public confirmCancelTrip(): void {
		if (!this.transaction?.TransferOrderId) {
			this.messageService.add({
				severity: 'error',
				summary: 'Error',
				detail: 'Transfer Order ID is required',
			});
			return;
		}

		const dto = {
			TransferOrderId: this.transaction.TransferOrderId,
			CancelReason: '' //TODO ?
		};

		this.xpassService.cancelTransferBooking(dto, this.messageService)
			.then(data => {
				if (!data.Success) {
					this.messageService.add({severity: 'error', summary: 'Error', detail: data.ErrorMessage});
					return;
				}

				this.loadTransactionDetails();

				this.showCancelTripDialog = false;
				this.messageService.add({
					severity: 'success', summary: 'Booking Cancelled',
					detail: 'The Xpass booking has been successfully cancelled and the user has been notified.'
				});
			})
			.catch((err: any) => {
				throw err;
			});
	}

	get maskedCustomerPhone(): string {
		return maskCustomerPhone(this.customerDetail?.phone);
	}

	openAddNotes() {
		this.showAddNotes = true;
		this.noteHeader = 'Add Note';
	}

	openReassignDriver() {
		this.showReassignDriver = true;
	}

	cancelAddNotes() {
		this.showAddNotes = false;
	}

	addTransferNote() {
		if (!this.transaction?.TransferOrderId) {
			this.messageService.add({
				severity: 'error',
				summary: 'Error',
				detail: 'Transfer Order ID is required',
			});
			return;
		}

		const dto = {
			TransferOrderId: this.transaction.TransferOrderId,
			Note: this.transferNote
		};

		this.xpassService.addNote(dto, this.messageService)
			.then(data => {
				if (!data.Success) {
					this.messageService.add({severity: 'error', summary: 'Error', detail: data.ErrorMessage});
					return;
				}

				this.loadTransactionDetails();

				this.showAddNotes = false;
				this.messageService.add({
					severity: 'success', summary: 'Note Added Successfully',
					detail: 'Your note has been saved and attached to the booking.'
				});
			})
			.catch((err: any) => {
				throw err;
			});
	}

	regenerateQrCode(transferOrderId: number) {
		this.regeneratingQrCode = true;

		const request: RegenerateQrCodeRequest = {
			TransferOrderId: transferOrderId
		};
		this.xpassAdminService.regenerateTransferOrder(request, this.messageService)
			.then(data => {
				if (!data.Success) {
					this.messageService.add({severity: 'error', summary: 'Error', detail: data.ErrorMessage});
					this.regeneratingQrCode = false;
					return;
				}

				this.messageService.add({
					severity: 'success',
					summary: 'QR Code Regenerated',
					detail: 'The QR code has been successfully regenerated.'
				});

				// Reload the details to get updated information
				this.loadTransactionDetails();

				this.regeneratingQrCode = false;
			})
			.catch((error: any) => {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Failed to regenerate QR code'
				});
				this.regeneratingQrCode = false;
			});
	}

	private getTripDirection(apiTripType?: string): TripDirection {
		if (apiTripType === ApiTripType.DEPARTURE_ONLY_TRIP) {
			return TripDirection.Departure;
		} else if (apiTripType === ApiTripType.ARRIVAL_ONLY_TRIP) {
			return TripDirection.Arrival;
		}
		return TripDirection.None;
	}

	private getTripType(apiTripType?: string): TripType {
		if (apiTripType === ApiTripType.ROUND_TRIP) {
			return TripType.RoundTrip;
		} else if (apiTripType === ApiTripType.DEPARTURE_ONLY_TRIP) {
			return TripType.OneWay;
		} else if (apiTripType === ApiTripType.ARRIVAL_ONLY_TRIP) {
			return TripType.OneWay;
		}
		return TripType.None;
	}
}
